import { Hint } from "@/components/ui/design-system/text";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { formatUsd } from "../formatUsd";
import { PROSE } from "./chrome";
import { Card, Money } from "./ui";

/**
 * The money, as two figures with one shape.
 *
 * "Outstanding" is an accounting word for the reader's own money, and it was
 * the only label — the amount already paid trailed after it as a sentence, so
 * the two numbers had different shapes and could not be compared at a glance.
 * Both are now a label over a figure, the same way round.
 *
 * One card on Home and on Videos. Videos used to close with its own line of
 * the same two numbers in a different shape, so the same question got two
 * answers that did not look alike.
 */
export function EarningsCard({
  outstandingUsd,
  paidUsd,
  payoutRequestMinimumUsd,
  payoutSpendSharePct,
  note,
  noteClassName,
  className,
}: {
  outstandingUsd: number;
  paidUsd: number;
  /** The payout formula. Left out until the program has loaded. */
  payoutRequestMinimumUsd?: number;
  payoutSpendSharePct?: number;
  /** A line under the formula, for what only one page needs to say. */
  note?: string;
  /**
   * Placement for the footnote, from the page that owns the card.
   *
   * Videos passes `max-lg:hidden`. The formula is the same sentence Home
   * already carries, and on a phone the two cards are one scroll apart, so
   * repeating it there buys nothing and costs three lines above the gallery
   * a creator came to look at. At desk width the card has the room and the
   * two pages are not seen together, so it stays.
   */
  noteClassName?: string;
  className?: string;
}) {
  const t = useCreatorTranslations("home");
  // A plus reads as money that arrived rather than as a second balance. At
  // zero it stays plain: a green +$0 congratulates someone on nothing, and the
  // colour has to keep meaning something for the first real payment to land.
  const paidLabel = paidUsd > 0 ? `+${formatUsd(paidUsd)}` : formatUsd(paidUsd);

  return (
    <Card
      className={cn("flex flex-col justify-between gap-nf-ds-5", className)}
    >
      {/*
        Two figures, each under its own name, at one size.

        THE SAME SIZE, DELIBERATELY. One of them was carried larger and the
        pair stopped being a pair: a big number beside a small one reads as a
        headline with a footnote, not as two facts about the same account. The
        difference is carried by colour, which is the house rule anyway.
      */}
      <div className="grid grid-cols-2 items-start gap-nf-ds-4">
        <Money
          label={t("earnings.dueToYou")}
          value={formatUsd(outstandingUsd)}
        />
        <span className={cn(paidUsd > 0 && "text-nf-ds-text-success")}>
          <Money label={t("earnings.paidSoFar")} value={paidLabel} />
        </span>
      </div>

      {payoutRequestMinimumUsd !== undefined &&
      payoutSpendSharePct !== undefined ? (
        <Hint muted className={cn(PROSE, "text-pretty", noteClassName)}>
          {t("earnings.formula", {
            amount: formatUsd(payoutRequestMinimumUsd),
            share: payoutSpendSharePct,
          })}
          {note ? ` ${note}` : null}
        </Hint>
      ) : note ? (
        <Hint muted className={cn(PROSE, "text-pretty", noteClassName)}>
          {note}
        </Hint>
      ) : null}
    </Card>
  );
}
